import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';

import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import { Spinner } from 'components/Spinner/Spinner';
import { userRoles } from 'constants/users';
import { Button } from 'components/Button/RoundButton';
import { Container, Wrapper } from 'components/Layout/Layout';
import TabSelector from 'components/TabSelector/TabSelector';
import { Header, HeadingContainer } from 'components/PageTitleHeader/styles';
import { HeadingButtonContainer } from '../cmsAllChallenges/styles';
import { TabAndComponentContainer } from './styles';
import NameAndDuration from './components/nameAndDuration';
import TextEditor from './components/textEditor';
import Branding from './components/branding';
import RunTournament from './components/runTournament';
import Sponsor from './components/sponsor';

const getDefaultTabItems = (updateFunc) => [
  {
    label: 'Contest Name and Duration',
    component: <NameAndDuration updateContestDetails={updateFunc} />,
  },
  {
    label: 'Landing Page',
    component: <TextEditor field={'landingPageContent'} updateContestDetails={updateFunc} />,
  },
  {
    label: 'Branding',
    component: <Branding updateContestDetails={updateFunc} />,
  },
  {
    label: 'Candidate Description',
    component: <TextEditor field={'candidateDescription'} updateContestDetails={updateFunc} />,
  },
  {
    label: 'Company Description',
    component: <TextEditor field={'companyDescription'} updateContestDetails={updateFunc} />,
  },
];

export default React.memo(() => {
  const { contestId } = useParams();
  const navigate = useNavigate();

  let { loading, error, contest, isContestUpdated } = useSelector((state) => state.CMSEditContestReducer);
  let { userRole } = useSelector((state) => state.auth);

  const { getContestForCMS, updateContest, resetUpdatedIds } = useActions(actions);

  const [contestDetails, setContestDetails] = useState({});

  const updateContestDetails = (changes) => setContestDetails((oldValue) => ({ ...oldValue, ...changes }));
  const [tabItems, setTabItems] = useState(getDefaultTabItems(updateContestDetails));

  useEffect(() => {
    if (contestId) getContestForCMS(contestId);
  }, [getContestForCMS, contestId]);

  useEffect(() => {
    if (!contest?._id) return;
    setContestDetails(JSON.parse(JSON.stringify(contest)));
    if (!contest.isTournament) return;
    setTabItems((current) =>
      current.some((item) => item.label === 'Run Cron')
        ? current
        : [
            ...current,
            {
              label: 'Run Cron',
              component: <RunTournament updateContestDetails={updateContestDetails} />,
            },
          ]
    );
  }, [contest]);

  useEffect(() => {
    if (userRole !== userRoles.SUPER_ADMIN) return;
    setTabItems((current) =>
      current.some((item) => item.label === 'Sponsor')
        ? current
        : [
            ...current,
            {
              label: 'Sponsor',
              component: <Sponsor updateContestDetails={updateContestDetails} />,
            },
          ]
    );
  }, [userRole]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    if (isContestUpdated) {
      toast.success('Contest updated successfully');
      resetUpdatedIds();
    }
  }, [isContestUpdated, resetUpdatedIds]);

  const updateContestWithValidation = () => {
    const currentDate = moment().format('YYYY-MM-DD');
    let isActiveSponsor = false;
    if (moment(contest.sponsorStartDate).isBefore(currentDate) && moment(contest.sponsorEndDate).isAfter(currentDate)) {
      isActiveSponsor = true;
    }

    if (
      contest.sponsorId &&
      isActiveSponsor &&
      (contest.sponsorStartDate !== contestDetails.sponsorStartDate ||
        contest.sponsorCompany !== contestDetails.sponsorCompany)
    ) {
      return toast.error('Cannot change start date or company for the active sponsored contest');
    }

    if (contestDetails.sponsorStartDate || contestDetails.sponsorEndDate || contestDetails.sponsorCompany) {
      if (!contestDetails.sponsorStartDate) return toast.error('Sponsor info missing start date');
      if (!contestDetails.sponsorEndDate) return toast.error('Sponsor info missing end date');
      if (!contestDetails.sponsorCompany) return toast.error('Sponsor info missing company');
      if (moment(contestDetails.sponsorStartDate).isSameOrAfter(contestDetails.sponsorEndDate))
        return toast.error('Sponsor end date must after start date');
      if (!isActiveSponsor && moment(contestDetails.sponsorStartDate).isBefore(currentDate))
        return toast.error('New sponsor start date must be greater at least current date');
    }

    if (moment(contestDetails.startDate).isSameOrAfter(contestDetails.endDate))
      return toast.error('Contest end date must be after start date');
    if (moment(currentDate).isSameOrAfter(contestDetails.endDate)) return toast.error('End date already passed');

    if (moment(contest.startDate).isBefore(currentDate) && contest.startDate !== contestDetails.startDate)
      return toast.error('Cant change start date on active contest');

    updateContest(contestDetails);
  };

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100vh">
      <Container column>
        {loading && <Spinner />}
        {!loading && (
          <>
            <HeadingContainer>
              <Header>{contest?.name}</Header>
              <HeadingButtonContainer>
                <Button
                  small
                  primaryBlue
                  primaryGreenBorder
                  margin="5px"
                  onClick={() => navigate(`/cms/AllContests/${contest.test}`)}
                >
                  <i className="fa fa-arrow-left" style={{ marginRight: '5px' }} />
                  Back
                </Button>
                <Button small primaryBlue primaryGreenBorder margin="5px" onClick={() => updateContestWithValidation()}>
                  <i className="fa-regular fa-floppy-disk" style={{ marginRight: '5px' }} />
                  Save
                </Button>
              </HeadingButtonContainer>
            </HeadingContainer>
            <TabAndComponentContainer>
              <TabSelector isReportPage items={tabItems} />
            </TabAndComponentContainer>
          </>
        )}
      </Container>
    </Wrapper>
  );
});
